import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const languages = [
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
];

export default function LanguageSelector() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = languages.find((l) => l.code === i18n.language) ?? languages[0];

  const handleSelect = (code: string) => {
    i18n.changeLanguage(code);
    setOpen(false);
    // Update SEO title dynamically
    const titles: Record<string, string> = {
      es: 'ZS Cars Import | Importación de coches',
      en: 'ZS Cars Import | Car Import Specialists',
    };
    document.title = titles[code] ?? titles.es;
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 rounded-xl border border-white/10 bg-white/5 backdrop-blur-md hover:border-purple-500/40 hover:bg-white/10 transition-all duration-300 cursor-pointer group"
        aria-label="Select language"
      >
        <span className="text-base leading-none">{current.flag}</span>
        <span className="text-white/70 text-xs font-medium tracking-widest uppercase group-hover:text-white transition-colors duration-300">
          {current.code.toUpperCase()}
        </span>
        <i
          className={`ri-arrow-down-s-line text-white/40 text-sm transition-transform duration-300 group-hover:text-white/70 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-40 rounded-xl border border-white/10 bg-[#0a0a0a]/95 backdrop-blur-xl overflow-hidden z-50 shadow-lg shadow-black/50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-all duration-200 cursor-pointer ${
                lang.code === i18n.language
                  ? 'bg-purple-900/30 text-white border-l-2 border-purple-500'
                  : 'text-white/60 hover:bg-white/5 hover:text-white border-l-2 border-transparent'
              }`}
            >
              <span className="text-base">{lang.flag}</span>
              <span className="font-medium tracking-wide">{lang.label}</span>
              {lang.code === i18n.language && (
                <i className="ri-check-line text-purple-400 ml-auto text-xs" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}